import { HttpInterceptor, HttpErrorResponse, HttpRequest, HttpHandler, HttpEvent, } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { LocalStoreManager } from '../../../shared/services/local-store-manager.service';
import { DBkeys } from '../models/dbKeys';
import { AuthService } from './auth.service';


@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshingToken = false;
    private refreshTokenSubject = new BehaviorSubject<string>(null);

    constructor(private localStorage: LocalStoreManager, private _router: Router, private authService: AuthService) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.isTokenEndpointRequest(request)) {
            return next.handle(request);
        }

        if (this.authService.isSessionExpired && this.authService.canSilentlyRefreshSession) {
            return this.refreshAndRetry(request, next);
        }

        return this.sendAuthorizedRequest(request, next);
    }

    private sendAuthorizedRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const accessToken = this.localStorage.getData(DBkeys.ACCESS_TOKEN);
        const authorizedRequest = accessToken
            ? request.clone({ headers: request.headers.set('Authorization', 'Bearer ' + accessToken) })
            : request;

        return next.handle(authorizedRequest).pipe(
            catchError((err: any) => {
                if (err instanceof HttpErrorResponse && err.status === 401) {
                    if (this.authService.canSilentlyRefreshSession) {
                        return this.refreshAndRetry(request, next);
                    }

                    this.redirectToLogin();
                }

                return throwError(err);
            })
        );
    }

    private refreshAndRetry(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.isRefreshingToken) {
            return this.refreshTokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(() => this.sendAuthorizedRequest(request, next))
            );
        }

        this.isRefreshingToken = true;
        this.refreshTokenSubject.next(null);

        return this.authService.refreshLogin().pipe(
            switchMap(() => {
                this.refreshTokenSubject.next(this.authService.accessToken);
                return this.sendAuthorizedRequest(request, next);
            }),
            catchError((err: any) => {
                this.redirectToLogin();
                return throwError(err);
            }),
            finalize(() => {
                this.isRefreshingToken = false;
            })
        );
    }

    private isTokenEndpointRequest(request: HttpRequest<any>): boolean {
        return request.url.toLowerCase().includes('/connect/token');
    }

    private redirectToLogin(): void {
        this.authService.loginRedirectUrl = this._router.url;
        this._router.navigateByUrl('/login');
    }
}


